import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Wallet } from "lucide-react";

const FOLIO_TYPES = ["master", "guest", "company", "group", "extra"];

function fmtMinor(minor, cur = "INR") {
  if (minor === null || minor === undefined) return "—";
  const n = Number(minor) / 100;
  return `${cur} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const statusBadge = { open: "info", closed: "success", void: "error" };

export default function Folios() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    property_id: "", stay_id: "", folio_type: "guest",
    bill_to_name: "", bill_to_email: "", bill_to_gstin: "", bill_to_address: "",
    currency_code: "INR", purpose: "",
  });

  const load = async () => {
    try {
      const q = new URLSearchParams();
      if (status) q.append("status", status);
      if (propertyId) q.append("property_id", propertyId);
      q.append("page_size", "50");
      const r = await api.get(`/folios?${q.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, propertyId]);

  const create = async () => {
    if (!form.property_id) { toast.error("Property ID is required"); return; }
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => payload[k] === "" && delete payload[k]);
      await api.post("/folios", payload);
      toast.success("Folio created");
      setOpen(false);
      setForm({ ...form, stay_id: "", bill_to_name: "", bill_to_email: "", bill_to_gstin: "", purpose: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="folios-page">
      <PageHeader
        icon={Wallet}
        title="Folios"
        subtitle={`${total} folio(s) — the guest's live financial workspace`}
        action={
          <Button data-testid="folio-create-btn" onClick={() => setOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> New Folio
          </Button>
        }
      />

      <Card>
        <div className="flex flex-wrap gap-3 items-end p-3">
          <div>
            <Label className="text-[10px] uppercase text-neutral-500">Property</Label>
            <Input
              className="w-40 h-9 mono"
              placeholder="property_id"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              data-testid="folio-property-filter"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-neutral-500">Status</Label>
            <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 h-9" data-testid="folio-status-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-900/40 text-neutral-400 text-[11px] uppercase">
              <tr>
                <th className="text-left px-3 py-2">Folio No</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Property</th>
                <th className="text-left px-3 py-2">Stay</th>
                <th className="text-left px-3 py-2">Bill To</th>
                <th className="text-right px-3 py-2">Balance</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} className="border-t border-neutral-800/60 hover:bg-neutral-900/30">
                  <td className="px-3 py-2 mono">
                    <Link to={`/finance/folios/${f.id}`} className="hover:underline text-orange-400"
                          data-testid={`folio-link-${f.folio_no}`}>
                      {f.folio_no}
                    </Link>
                  </td>
                  <td className="px-3 py-2"><Badge tone="neutral">{f.folio_type}</Badge></td>
                  <td className="px-3 py-2 mono text-[11px]">{f.property_id}</td>
                  <td className="px-3 py-2 mono text-[11px]">{f.stay_id || "—"}</td>
                  <td className="px-3 py-2">{f.bill_to?.name || "—"}</td>
                  <td className="px-3 py-2 text-right mono">{fmtMinor(f.balance_minor_cache, f.currency_code)}</td>
                  <td className="px-3 py-2"><Badge tone={statusBadge[f.status] || "neutral"}>{f.status}</Badge></td>
                  <td className="px-3 py-2 mono text-[11px] text-neutral-500">{(f.created_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-neutral-500">No folios yet — create one to start.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Folio</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div>
              <Label>Property ID *</Label>
              <Input value={form.property_id} onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                     data-testid="folio-form-property" />
            </div>
            <div>
              <Label>Stay ID (optional)</Label>
              <Input value={form.stay_id} onChange={(e) => setForm({ ...form, stay_id: e.target.value })}
                     data-testid="folio-form-stay" />
            </div>
            <div>
              <Label>Folio type</Label>
              <Select value={form.folio_type} onValueChange={(v) => setForm({ ...form, folio_type: v })}>
                <SelectTrigger data-testid="folio-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FOLIO_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency</Label>
              <Input value={form.currency_code} onChange={(e) => setForm({ ...form, currency_code: e.target.value })}
                     className="mono" />
            </div>
            <div className="col-span-2">
              <Label>Bill To — Name</Label>
              <Input value={form.bill_to_name} onChange={(e) => setForm({ ...form, bill_to_name: e.target.value })}
                     data-testid="folio-form-billto" />
            </div>
            <div>
              <Label>Bill To — Email</Label>
              <Input value={form.bill_to_email} onChange={(e) => setForm({ ...form, bill_to_email: e.target.value })} />
            </div>
            <div>
              <Label>Bill To — GSTIN</Label>
              <Input value={form.bill_to_gstin} onChange={(e) => setForm({ ...form, bill_to_gstin: e.target.value })}
                     className="mono" />
            </div>
            <div className="col-span-2">
              <Label>Bill To — Address</Label>
              <Input value={form.bill_to_address} onChange={(e) => setForm({ ...form, bill_to_address: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="folio-form-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
